import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useProductCategories } from '../../../hooks/useProductCategories';
import CategorySelectField from './CategorySelectField';
import InvoiceDescriptionLabelHelp from './InvoiceDescriptionHelp';
import { HOURLY_RECURRING, formatRecurring } from '../../../utils/money';
import { SPECIAL_CATEGORY_NAME } from '../../../types/models/SpecialDeal';
import type {
  AdminProductKeyCheck,
  ApiResponseAdminProductCreate,
  ApiResponseAdminProductKeyCheck,
} from '../../../types/api/ApiResponseAdminProduct';
import type { AdminProduct } from '../../../types/models/AdminProduct';

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create so the parent can refresh the catalog grid. */
  onCreated: () => void;
  /**
   * Clone mode: seeds every cloneable field (label, descriptions, cadence, category, customer
   * visibility) from this product. The machine key is deliberately NOT cloned — it is unique and
   * immutable, so the user must choose a fresh one (the live availability check guides them).
   * Mount keyed by the source product id upstream so each clone starts from a fresh seed.
   */
  cloneSource?: AdminProduct;
}

// The billing cadences the backend accepts (the `_Product::$recurring` enum). Mirrored client-side so
// the select can only offer valid values; the backend re-validates and is the final authority.
const RECURRING_OPTIONS = ['once', HOURLY_RECURRING, 'monthly', 'yearly'];

// Backend column limits (`_Product` setters). Enforced client-side to give inline feedback and disable
// Save early; the backend re-validates and its field error (data.{field}) still wins if we're wrong.
const LABEL_MAX = 128;
const DESC_MAX = 255;

interface FieldErrors {
  product_key?: string;
  label?: string;
  product_desc?: string;
  recurring?: string;
  product_category_id?: string;
  public_desc?: string;
  active?: string;
  tenant_visible?: string;
  general?: string;
}

/**
 * Create a new catalog product (the title-bar "Create product" action → POST /admin/products). Every
 * field the create endpoint accepts is here; `id` (PK) is DB-assigned and the scalar default price is a
 * separate table (set from the grid after creation), so neither is collected here.
 *
 * The load-bearing field is **`product_key`** — the immutable machine key the billing code joins on.
 * The user types it naturally (spaces / lower-case fine); the backend normalises to the catalog
 * convention (UPPERCASE, spaces → `_`, unique). We validate it live against
 * GET /admin/products/key-available (debounced) so the user sees the normalised result and whether it's
 * free BEFORE submitting, and Save stays disabled until the key is valid + available.
 *
 * Mounted only while open (keyed upstream so each open starts blank).
 *
 * With `cloneSource` set, the same form doubles as the grid's "Clone" action: state seeds from the
 * source product instead of blank (safe — the modal is mounted only while open and keyed, so the
 * prop-seeded state can never be clobbered), and only the product key starts empty.
 */
function CreateProductModal({ isOpen, onClose, onCreated, cloneSource }: CreateProductModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useProductCategories(isOpen);

  // The key is never seeded from a clone — it is unique/immutable, so a clone still needs its own.
  const [productKey, setProductKey] = useState('');
  const [label, setLabel] = useState(cloneSource?.label ?? '');
  const [productDesc, setProductDesc] = useState(cloneSource?.product_desc ?? '');
  const [publicDesc, setPublicDesc] = useState(cloneSource?.public_desc ?? '');
  const [recurring, setRecurring] = useState(cloneSource?.recurring ?? '');
  const [categoryId, setCategoryId] = useState(cloneSource?.product_category_id ?? 0);
  // Default true (the backend default): most products belong on the customer pricing pages. Per-deal
  // rebate products (category "Special") are the typical case for creating hidden.
  const [tenantVisible, setTenantVisible] = useState(cloneSource?.tenant_visible ?? true);
  // Status: a new product is normally FORCED inactive — it can only be activated once its billing is
  // wired into the invoicing engine. The one exception is the Special category, whose products are
  // billed through deals (generic machinery, no per-product wiring), so they may — and typically
  // should — be created active. The `active` state only takes effect for a Special category.
  const [active, setActive] = useState(true);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Live product_key availability check (debounced). `keyCheck` is derived read-only state — it seeds
  // nothing editable, so a useApiFetch identity flip on token renew merely re-checks the same key.
  const [debouncedKey, setDebouncedKey] = useState('');
  const [keyCheck, setKeyCheck] = useState<AdminProductKeyCheck | null>(null);
  const [isCheckingKey, setIsCheckingKey] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedKey(productKey.trim()), 400);
    return () => clearTimeout(handle);
  }, [productKey]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (debouncedKey === '') {
      setKeyCheck(null);
      setIsCheckingKey(false);
      return;
    }
    let cancelled = false;
    setIsCheckingKey(true);
    void (async () => {
      try {
        const response = await apiFetch(
          `/admin/products/key-available?product_key=${encodeURIComponent(debouncedKey)}`,
        );
        // Branch on the JSend body.status, NEVER response.ok. The endpoint is non-throwing.
        const body = (await response.json().catch(() => null)) as ApiResponseAdminProductKeyCheck | null;
        if (!cancelled) {
          setKeyCheck(body?.status === 'success' && body.data?.key_check ? body.data.key_check : null);
        }
      } catch {
        if (!cancelled) {
          setKeyCheck(null);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingKey(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedKey, isOpen, apiFetch]);

  // Normalise inputs the same way the backend does before comparing / sending.
  const trimmedLabel = label.trim();
  const trimmedDesc = productDesc.trim();
  const normPublic = publicDesc.trim() === '' ? null : publicDesc.trim();

  // Client-side validation (matches the ProductStoreRequest / _Product rules).
  const labelError =
    trimmedLabel === '' ? 'Label is required.' : trimmedLabel.length > LABEL_MAX ? `Label cannot exceed ${LABEL_MAX} characters.` : undefined;
  const descError =
    trimmedDesc === ''
      ? 'Invoice description is required.'
      : trimmedDesc.length > DESC_MAX
        ? `Invoice description cannot exceed ${DESC_MAX} characters.`
        : undefined;
  const publicError =
    (normPublic?.length ?? 0) > DESC_MAX ? `Public description cannot exceed ${DESC_MAX} characters.` : undefined;

  // The result is only about the CURRENT input when its echoed `input` matches (during the debounce
  // window after an edit, keyCheck still holds the previous key's result — treat that as "pending").
  const trimmedKey = productKey.trim();
  const keyMatchesInput = keyCheck !== null && keyCheck.input === trimmedKey;
  // The key is ready only when the up-to-date check came back valid AND free (and isn't mid-check).
  const isKeyReady = !isCheckingKey && keyMatchesInput && keyCheck?.valid === true && keyCheck?.available === true;
  const isFormValid =
    isKeyReady && !labelError && !descError && !publicError && recurring !== '' && categoryId > 0;

  const clearError = (field: keyof FieldErrors) =>
    setErrors((prev) => (prev[field] || prev.general ? { ...prev, [field]: undefined, general: undefined } : prev));

  // The Status switch is editable only for the Special category (deal-billed — no wiring to wait
  // for); every other category keeps the forced-inactive posture. When cloning, fall back to the
  // source's category name while the categories list is still loading, since the select seeds
  // from it.
  const selectedCategoryName =
    categories.find((c) => c.id === categoryId)?.name ??
    (cloneSource && categoryId === cloneSource.product_category_id ? cloneSource.category.name : undefined);
  const isSpecialCategory = selectedCategoryName === SPECIAL_CATEGORY_NAME;
  const effectiveActive = isSpecialCategory ? active : false;
  // The backend refuses an hourly product in Special (deals can never carry one), so each field
  // disables the value that would clash with the other; this guard also keeps Save disabled.
  const isHourly = recurring === HOURLY_RECURRING;
  const hourlyInSpecial = isHourly && isSpecialCategory;
  const canSave = isFormValid && !hourlyInSpecial;

  const notifyError = (description: string) => {
    addNotification({
      title: 'Could not create product',
      srTitle: 'Error alert',
      description,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    setIsSaving(true);
    setErrors({});
    try {
      const response = await apiFetch('/admin/products', {
        method: 'POST',
        // Send the normalised key we confirmed available; the backend re-normalises idempotently.
        body: JSON.stringify({
          product_key: keyCheck?.product_key ?? productKey.trim(),
          label: trimmedLabel,
          product_desc: trimmedDesc,
          public_desc: normPublic,
          recurring,
          product_category_id: categoryId,
          // Explicit, since the backend defaults an omitted `active` to true: forced false for
          // every category except Special (deal-billed, so activation needs no code wiring).
          active: effectiveActive,
          tenant_visible: tenantVisible,
        }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductCreate | null;
      if (body?.status === 'success' && body.data?.product) {
        const created: AdminProduct = body.data.product;
        addNotification({
          title: 'Product created',
          srTitle: 'Success alert',
          description: `${created.label} (${created.product_key}) has been created. Set its default price from the grid.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onCreated();
        onClose();
        return;
      }
      const d = body?.data;
      const anyField =
        d?.product_key ||
        d?.label ||
        d?.product_desc ||
        d?.recurring ||
        d?.product_category_id ||
        d?.public_desc ||
        d?.active ||
        d?.tenant_visible;
      setErrors({
        product_key: d?.product_key,
        label: d?.label,
        product_desc: d?.product_desc,
        recurring: d?.recurring,
        product_category_id: d?.product_category_id,
        public_desc: d?.public_desc,
        active: d?.active,
        tenant_visible: d?.tenant_visible,
        general: anyField ? undefined : (body?.message ?? 'Could not create the product. Please try again.'),
      });
      notifyError(anyField || body?.message || 'Could not create the product. Please try again.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setErrors({ general: message });
      notifyError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // The live key-field status line beneath the persistent convention hint.
  const renderKeyStatus = () => {
    if (errors.product_key) {
      return <HelperTextItem variant="error">{errors.product_key}</HelperTextItem>;
    }
    if (trimmedKey === '') {
      return null;
    }
    // A check is in flight, or the input changed since the last result — the current key is unverified.
    if (isCheckingKey || !keyCheck || !keyMatchesInput) {
      return <HelperTextItem>Checking availability…</HelperTextItem>;
    }
    if (!keyCheck.valid || !keyCheck.available) {
      return <HelperTextItem variant="error">{keyCheck.reason ?? 'This key cannot be used.'}</HelperTextItem>;
    }
    return (
      <HelperTextItem variant="success">Will be created as “{keyCheck.product_key}” — available.</HelperTextItem>
    );
  };

  const keyHasError =
    Boolean(errors.product_key) || (keyMatchesInput && (keyCheck?.valid === false || keyCheck?.available === false));

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={cloneSource ? `Clone product ${cloneSource.label}` : 'Create product'}
    >
      <ModalHeader title={cloneSource ? `Clone product — ${cloneSource.label}` : 'Create product'} />
      <ModalBody>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          {errors.general && (
            <Alert variant="danger" isInline title="Could not create product">
              {errors.general}
            </Alert>
          )}

          <FormGroup label="Label" isRequired fieldId="create-product-label">
            <TextInput
              id="create-product-label"
              value={label}
              maxLength={LABEL_MAX}
              onChange={(_event, value) => {
                setLabel(value);
                clearError('label');
              }}
              validated={errors.label || labelError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Product label"
            />
            {(errors.label || labelError) && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{errors.label ?? labelError}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Product key" isRequired fieldId="create-product-key">
            <TextInput
              id="create-product-key"
              value={productKey}
              onChange={(_event, value) => {
                setProductKey(value);
                clearError('product_key');
              }}
              validated={keyHasError ? 'error' : isKeyReady ? 'success' : 'default'}
              isDisabled={isSaving}
              placeholder="e.g. POWER_METER"
              aria-label="Product key"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  Uppercase letters, digits and underscores only. Spaces become underscores (e.g. “power
                  meter” → POWER_METER), and multi-word keys must be joined with underscores. Must be
                  unique — and it can never be changed after creation.
                </HelperTextItem>
                {renderKeyStatus()}
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup
            label="Invoice description"
            isRequired
            fieldId="create-product-desc"
            labelHelp={<InvoiceDescriptionLabelHelp />}
          >
            <TextArea
              id="create-product-desc"
              value={productDesc}
              maxLength={DESC_MAX}
              rows={2}
              onChange={(_event, value) => {
                setProductDesc(value);
                clearError('product_desc');
              }}
              validated={errors.product_desc || descError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Invoice line-item description template"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.product_desc || descError ? 'error' : 'default'}>
                  {errors.product_desc ??
                    descError ??
                    'Line-item template. Insert {{tokens}} — filled in when the invoice line is generated (see the help icon).'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Public description" fieldId="create-product-public-desc">
            <TextArea
              id="create-product-public-desc"
              value={publicDesc}
              maxLength={DESC_MAX}
              rows={2}
              onChange={(_event, value) => {
                setPublicDesc(value);
                clearError('public_desc');
              }}
              validated={errors.public_desc || publicError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Customer-facing product description"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.public_desc || publicError ? 'error' : 'default'}>
                  {errors.public_desc ??
                    publicError ??
                    'Shown to customers on the pricing page (plain text, no tokens). Optional.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Billing cadence" isRequired fieldId="create-product-recurring">
            <FormSelect
              id="create-product-recurring"
              value={recurring}
              onChange={(_event, value) => {
                setRecurring(value);
                clearError('recurring');
              }}
              isDisabled={isSaving}
              validated={errors.recurring ? 'error' : 'default'}
              aria-label="Billing cadence"
            >
              <FormSelectOption value="" label="Select a cadence" isDisabled />
              {RECURRING_OPTIONS.map((option) => {
                const isBlocked = option === HOURLY_RECURRING && isSpecialCategory;
                return (
                  <FormSelectOption
                    key={option}
                    value={option}
                    label={
                      isBlocked
                        ? `${formatRecurring(option)} (not allowed in ${SPECIAL_CATEGORY_NAME})`
                        : formatRecurring(option)
                    }
                    isDisabled={isBlocked}
                  />
                );
              })}
            </FormSelect>
            {(errors.recurring || isHourly) && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.recurring || hourlyInSpecial ? 'error' : 'default'}>
                    {errors.recurring ??
                      (hourlyInSpecial
                        ? `An hourly product can't be in the ${SPECIAL_CATEGORY_NAME} category (deals can never carry one). Choose another category.`
                        : "Usage-billed: the default price is per hour. The cadence can't be changed to or from hourly later, because the other cadences are priced per period.")}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Category" isRequired fieldId="create-product-category">
            <CategorySelectField
              idPrefix="create-product"
              categories={categories}
              categoriesLoading={categoriesLoading}
              categoriesError={categoriesError}
              value={categoryId}
              onChange={(id) => {
                setCategoryId(id);
                clearError('product_category_id');
              }}
              refetchCategories={refetchCategories}
              isDisabled={isSaving}
              error={errors.product_category_id}
              placeholder="Select a category"
              disabledCategory={
                isHourly ? { name: SPECIAL_CATEGORY_NAME, reason: 'not allowed for hourly products' } : undefined
              }
              fallbackOption={
                cloneSource ? { id: cloneSource.product_category_id, name: cloneSource.category.name } : undefined
              }
            />
          </FormGroup>

          <FormGroup label="Status" fieldId="create-product-active">
            <Switch
              id="create-product-active"
              label="Active"
              isChecked={effectiveActive}
              onChange={(_event, checked) => {
                setActive(checked);
                clearError('active');
              }}
              isDisabled={isSaving || !isSpecialCategory}
              aria-label="Product active status"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.active ? 'error' : 'default'}>
                  {errors.active ??
                    (isSpecialCategory ? (
                      <>
                        Special products are billed through deals — no per-product wiring is needed, so they can
                        be created <strong>active</strong> right away (a deal can only reference an active
                        product).
                      </>
                    ) : (
                      <>
                        New products are always created <strong>inactive</strong>. A product can only be
                        activated once its billing is wired into the invoicing engine — but you can already use
                        it in quotes while it&apos;s inactive.
                      </>
                    ))}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Customer visibility" fieldId="create-product-tenant-visible">
            <Switch
              id="create-product-tenant-visible"
              label="Visible on customer pricing pages"
              isChecked={tenantVisible}
              onChange={(_event, checked) => {
                setTenantVisible(checked);
                clearError('tenant_visible');
              }}
              isDisabled={isSaving}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.tenant_visible ? 'error' : 'default'}>
                  {errors.tenant_visible ??
                    'Turn off for hidden-but-billable: the product stays off customer pricing pages while deals can still bill it. Per-deal rebate products are typically created hidden.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          isLoading={isSaving}
          isDisabled={isSaving || !canSave}
        >
          Create
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CreateProductModal;
